// OT Resilience screen: how much each worker's FED rises per overtime hour.
// Port of the prototype's renderResilience.

import React from 'react';
import { ArrowRight } from 'lucide-react';
import { GROUPS, REGIONS, wArr } from '../engine/data';
import type { Worker } from '../engine/data';
import { ScoreLegend } from '../ScoreWidgets';
import { useDash } from '../state';
import { Grid, Kpi, KpiRow, Panel, Pill, dataTable, fedTone, toneBg, toneText } from '../ui';
import type { Tone } from '../ui';
import { cn } from '@/lib/utils';
import { RegionPicker } from './scopePickers';

interface ResilWorker extends Worker {
  _otHrs: number;
  _rate: number | null;
  _resil: number | null;
  _resilLabel: string;
  _resilTone: Tone;
}

function resilData(workers: Worker[]): ResilWorker[] {
  const withOT = workers.filter((w) => w.otHrs > 0);
  const maxRate = withOT.length > 0 ? Math.max(...withOT.map((w) => w.fed / (w.otHrs || 1))) : 1;
  return workers
    .map((w): ResilWorker => {
      const otHrs = w.otHrs || 0;
      const rate = otHrs > 0 ? w.fed / otHrs : null;
      const score = rate !== null ? Math.round(Math.max(0, 100 - (rate / maxRate) * 100)) : null;
      const label = score === null ? 'No OT' : score >= 65 ? 'High' : score >= 35 ? 'Moderate' : 'Low';
      const tone: Tone = score === null ? 'muted' : score >= 65 ? 'good' : score >= 35 ? 'warn' : 'bad';
      return { ...w, _otHrs: otHrs, _rate: rate, _resil: score, _resilLabel: label, _resilTone: tone };
    })
    .sort((a, b) => {
      // Workers with OT by resilience desc; no-OT workers at the bottom.
      if (a._resil === null && b._resil === null) return 0;
      if (a._resil === null) return 1;
      if (b._resil === null) return -1;
      return b._resil - a._resil;
    });
}

/** Average FED per OT hour across workers with OT. */
function avgRate(ws: ResilWorker[]): number | null {
  const f = ws.filter((w) => w._otHrs > 0 && w._rate !== null);
  return f.length ? f.reduce((a, w) => a + (w._rate ?? 0), 0) / f.length : null;
}

const countHigh = (ws: ResilWorker[]) => ws.filter((w) => w._resil !== null && w._resil >= 65).length;
const countLow = (ws: ResilWorker[]) => ws.filter((w) => w._resil !== null && w._resil < 35).length;
const fmtRate = (r: number | null) => (r !== null ? r.toFixed(1) : '—');

const ResilBar: React.FC<{ score: number | null; tone: Tone }> = ({ score, tone }) => {
  if (score === null) return <span className='text-[11px] text-muted-foreground'>No OT</span>;
  return (
    <div className='flex items-center gap-1.5'>
      <div className='h-1.5 w-[60px] overflow-hidden rounded-full bg-border'>
        <div className={cn('h-full rounded-full', toneBg[tone])} style={{ width: `${score}%` }} />
      </div>
      <span className={cn('text-xs font-bold', toneText[tone])}>{score}</span>
    </div>
  );
};

const ResilTable: React.FC<{ ws: ResilWorker[]; title: string; subtitle?: string }> = ({ ws, title, subtitle }) => {
  const { drillWorker } = useDash();
  const { th, td, num, tr } = dataTable;
  return (
    <Panel className='mb-5' title={title} sub={subtitle}>
      <div className={dataTable.wrap}>
        <table className={dataTable.table}>
          <thead>
            <tr>
              <th className={cn(th, 'w-7')} />
              <th className={th}>Worker</th>
              <th className={cn(th, num)}>OT Hrs</th>
              <th className={cn(th, num)}>FED</th>
              <th className={cn(th, num)} title='FED points per OT hour'>
                FED/hr
              </th>
              <th className={th}>Resilience Index</th>
              <th className={th}>Band</th>
            </tr>
          </thead>
          <tbody>
            {ws.map((w, i) => (
              <tr key={w.init} onClick={() => drillWorker(w.init)} className={cn(tr, 'cursor-pointer')}>
                <td className={cn(td, 'w-7 pr-2 text-right font-semibold text-muted-foreground')}>
                  {w._resil !== null ? i + 1 : '-'}
                </td>
                <td className={td}>
                  <div className='text-[13px] font-semibold'>{w.name}</div>
                  <div className='text-[11px] text-muted-foreground'>
                    {w.role} · {GROUPS[w.group].name}
                  </div>
                </td>
                <td className={cn(td, num)}>{w._otHrs > 0 ? `${w._otHrs}h` : '—'}</td>
                <td className={cn(td, num, 'font-semibold', toneText[fedTone(w.fed)])}>{w.fed}</td>
                <td className={cn(td, num)}>{fmtRate(w._rate)}</td>
                <td className={td}>
                  <ResilBar score={w._resil} tone={w._resilTone} />
                </td>
                <td className={td}>
                  <Pill tone={w._resilTone}>{w._resilLabel}</Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const ResilKpis: React.FC<{ ws: ResilWorker[] }> = ({ ws }) => {
  const withOT = ws.filter((w) => w._otHrs > 0);
  const low = countLow(ws);
  return (
    <KpiRow className='mb-5'>
      <Kpi label='With OT' value={withOT.length} sub={`of ${ws.length} this week`} />
      <Kpi label='High Resilience' value={countHigh(ws)} tone='good' sub='safe to extend' />
      <Kpi label='Low Resilience' value={low} tone={low > 0 ? 'bad' : undefined} sub='OT risk — review' />
      <Kpi label='Avg FED/OT hr' value={fmtRate(avgRate(ws))} sub='team baseline rate' />
    </KpiRow>
  );
};

/** Clickable summary card (region or group) with High / Low / FED-per-OT-hour. */
const DrillCard: React.FC<{
  title: string;
  titleColor?: string;
  highLabel: string;
  lowLabel: string;
  ws: ResilWorker[];
  onClick: () => void;
}> = ({ title, titleColor, highLabel, lowLabel, ws, onClick }) => {
  const high = countHigh(ws);
  const low = countLow(ws);
  return (
    <Panel className='cursor-pointer transition-colors hover:bg-muted/40'>
      <div role='button' tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === 'Enter' && onClick()}>
        <div className='mb-2 flex items-center justify-between'>
          <div className='text-[13px] font-semibold' style={titleColor ? { color: titleColor } : undefined}>
            {title}
          </div>
          <ArrowRight className='h-3.5 w-3.5 text-muted-foreground' />
        </div>
        <div className='flex gap-4 pb-1'>
          <div>
            <div className='text-xl font-bold text-success'>{high}</div>
            <div className='text-[10px] text-muted-foreground'>{highLabel}</div>
          </div>
          <div>
            <div className={cn('text-xl font-bold', low > 0 ? 'text-destructive' : 'text-muted-foreground')}>{low}</div>
            <div className='text-[10px] text-muted-foreground'>{lowLabel}</div>
          </div>
          <div>
            <div className='text-xl font-bold text-muted-foreground'>{fmtRate(avgRate(ws))}</div>
            <div className='text-[10px] text-muted-foreground'>FED/OT hr</div>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export const ResilienceScreen: React.FC = () => {
  const d = useDash();
  const allW = wArr();

  const header = (
    <div className='mb-4'>
      <div className='text-base font-semibold'>OT Resilience</div>
      <div className='mb-3 text-xs text-muted-foreground'>
        FED response to overtime — ranked by how much each worker&apos;s fatigue rises per hour worked above contract
      </div>
      <ScoreLegend />
    </div>
  );

  if (d.drillScope === 'company') {
    const ranked = resilData(allW);
    return (
      <div>
        {header}
        <ResilKpis ws={ranked} />
        <Grid cols={2} className='mb-5'>
          {Object.entries(REGIONS).map(([rid, reg]) => (
            <DrillCard
              key={rid}
              title={reg.name}
              titleColor={reg.color}
              highLabel='High resil.'
              lowLabel='Low resil.'
              ws={resilData(allW.filter((w) => reg.groups.includes(w.group)))}
              onClick={() => d.drillRegion(rid)}
            />
          ))}
        </Grid>
        <ResilTable
          ws={ranked}
          title='All Staff — OT Resilience Ranking'
          subtitle='Ranked by resilience index (high = FED rises least per OT hour). Click any row to view individual detail.'
        />
      </div>
    );
  }

  if (d.drillScope === 'region' && d.activeRegion) {
    const reg = REGIONS[d.activeRegion];
    const rw = resilData(allW.filter((w) => reg.groups.includes(w.group)));
    return (
      <div>
        {header}
        <RegionPicker />
        <ResilKpis ws={rw} />
        <Grid cols={2} className='mb-5'>
          {reg.groups.map((gid) => (
            <DrillCard
              key={gid}
              title={GROUPS[gid].name}
              highLabel='High'
              lowLabel='Low'
              ws={resilData(allW.filter((w) => w.group === gid))}
              onClick={() => d.drillGroup(gid)}
            />
          ))}
        </Grid>
        <ResilTable
          ws={rw}
          title={`${reg.name} — OT Resilience Ranking`}
          subtitle='Ranked within region. FED/hr = fatigue points per overtime hour.'
        />
      </div>
    );
  }

  if (d.drillScope === 'group' && d.activeGroup !== null) {
    const gid = d.activeGroup;
    const regObj = Object.values(REGIONS).find((r) => r.groups.includes(gid));
    const gw = resilData(allW.filter((w) => w.group === gid));
    return (
      <div>
        {header}
        {regObj && (
          <div className='mb-4 flex flex-wrap gap-2'>
            {regObj.groups.map((g) => (
              <button
                key={g}
                type='button'
                onClick={() => d.patch({ activeGroup: g })}
                className={cn(
                  'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
                  g === gid
                    ? 'border-success/30 bg-success/10 text-success'
                    : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {GROUPS[g].name}
              </button>
            ))}
          </div>
        )}
        <ResilKpis ws={gw} />
        <ResilTable
          ws={gw}
          title={`${GROUPS[gid].name} — OT Resilience`}
          subtitle='All workers in this group ranked by resilience. Click a row to view their individual OT Resilience card.'
        />
      </div>
    );
  }

  // Prototype renders only the header for individual scope.
  return <div>{header}</div>;
};
